import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Download, CheckCircle2, XCircle, Loader2, ChevronRight, ChevronsUpDown, Wrench } from 'lucide-react';
import baseapi from '../../../services/baseapi';

interface ConfigItem {
  db_type?: string;
  name: string;
  setting: any;
  unit?: string;
  source?: string;
  short_desc?: string;
  category?: string;
  collected_at?: string;
}

interface ConfigurationViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
}

export const ConfigurationView: React.FC<ConfigurationViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<ConfigItem[]>([]);
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    try {
      const res = await baseapi.get('/db_monitor_dashboard/configuration', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      const data = res.data?.data || res.data;
      const settingsList = data?.settings || (Array.isArray(data) ? data : []);
      setSettings(settingsList);
    } catch (err) {
      console.warn('ConfigurationView fetch error:', err);
      setSettings([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const filtered = useMemo(() => {
    return settings.filter((s) => {
      const cat = s.category || 'General';
      return (
        (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.short_desc || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.setting !== undefined && String(s.setting).toLowerCase().includes(search.toLowerCase())) ||
        cat.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [settings, search]);

  const groupedSettings = useMemo(() => {
    const map = new Map<string, ConfigItem[]>();
    filtered.forEach((item) => {
      const cat = item.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    });
    return Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [filtered]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleCollapseAll = () => {
    if (collapsedCategories.size === groupedSettings.length) {
      setCollapsedCategories(new Set());
    } else {
      setCollapsedCategories(new Set(groupedSettings.map((g) => g.category)));
    }
  };

  const exportJSON = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(settings, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `db_configuration_${activeCluster || 'default'}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-3 relative">

      {/* Main Glassmorphism Container */}
      <div className="bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90 border border-gray-700/60 rounded-xl p-3.5 shadow-2xl backdrop-blur-xl space-y-3">

        {/* Top Header & Search Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-800 pb-2.5">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded bg-orange-500  text-white">
              <Wrench className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white tracking-wide">Runtime Configuration Parameters</h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-400 border border-orange-500/30">
                  {filtered.length} / {settings.length}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Input */}
            <div className="relative w-full sm:w-56">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parameter or description..."
                className="w-full bg-gray-950/80 border border-gray-700/80 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-7"
              />
            </div>

            {/* Expand / Collapse All */}
            {groupedSettings.length > 0 && (
              <button
                onClick={toggleCollapseAll}
                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-800/80 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700/70 text-xs font-semibold transition-all shrink-0 h-7"
                title="Expand/Collapse All Categories"
              >
                <ChevronsUpDown className="w-3.5 h-3.5 text-orange-400" />
                <span className="hidden sm:inline">
                  {collapsedCategories.size === groupedSettings.length ? 'Expand All' : 'Collapse All'}
                </span>
              </button>
            )}

            {/* Export Button */}
            <button
              onClick={exportJSON}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-800/80 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700/70 text-xs font-semibold transition-all shrink-0 shadow-sm h-7"
              title="Export configuration as JSON"
            >
              <Download className="w-3.5 h-3.5 text-orange-400" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* Compact Parameter Table with Collapsible Categories */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-800 rounded-lg max-h-[calc(100vh-210px)]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-950 border-b border-gray-800 text-[10px] text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap shadow-sm">
                <th className="py-2 px-3 w-64 bg-gray-950">Parameter</th>
                <th className="py-2 px-3 w-36 bg-gray-950">Value</th>
                <th className="py-2 px-3 w-28 bg-gray-950">Source</th>
                <th className="py-2 px-3 w-40 whitespace-nowrap bg-gray-950">Last Changed</th>
                <th className="py-2 px-3 bg-gray-950">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40 text-gray-200 bg-gray-900/20">
              {groupedSettings.length > 0 ? (
                groupedSettings.map((group) => {
                  const isCollapsed = collapsedCategories.has(group.category);

                  return (
                    <React.Fragment key={group.category}>
                      {/* Category Header Row */}
                      <tr
                        onClick={() => toggleCategory(group.category)}
                        className="bg-gray-800 hover:bg-gray-800/80 border-t border-b border-gray-800 cursor-pointer transition-colors select-none"
                      >
                        <td colSpan={5} className="py-1.5 px-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ChevronRight
                                className={`w-3.5 h-3.5 text-orange-400 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'
                                  }`}
                              />
                              <span className="text-xs font-bold text-white tracking-wide">
                                {group.category}
                              </span>
                              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-400 border border-orange-500/30">
                                {group.items.length}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* Category Items */}
                      {!isCollapsed &&
                        group.items.map((s, idx) => {
                          const valStr = s.setting !== undefined && s.setting !== null ? String(s.setting) : '';
                          const isBool = valStr === 'on' || valStr === 'off' || valStr === 'true' || valStr === 'false';
                          const isOn = valStr === 'on' || valStr === 'true';

                          return (
                            <tr key={idx} className="hover:bg-gray-800/40 transition-colors border-b border-gray-800/30 h-8">
                              {/* Parameter Name */}
                              <td className="py-1 px-3 font-mono font-bold text-orange-400 text-[11px] pl-7 whitespace-nowrap">
                                {s.name}
                              </td>

                              {/* Value with unit */}
                              <td className="py-1 px-3 whitespace-nowrap max-w-[150px]">
                                {isBool ? (
                                  <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-mono font-bold border ${isOn
                                        ? 'bg-green-500/10 text-green-400 border-green-500/30'
                                        : 'bg-gray-800 text-gray-400 border-gray-700'
                                      }`}
                                  >
                                    {isOn ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <XCircle className="w-3 h-3 text-gray-400" />}
                                    {valStr}
                                  </span>
                                ) : (
                                  <div
                                    className="flex items-center gap-1 font-mono max-w-[140px] truncate"
                                    title={s.unit ? `${valStr} ${s.unit}` : valStr}
                                  >
                                    <span className="font-bold text-white text-[11px] truncate">{valStr || '-'}</span>
                                    {s.unit && (
                                      <span className="text-[9px] text-gray-400 bg-gray-950 px-1 py-0.2 rounded border border-gray-800 font-mono shrink-0">
                                        {s.unit}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>

                              {/* Source */}
                              <td className="py-1 px-3 font-mono text-[10px] text-gray-400 whitespace-nowrap">
                                {s.source ? (
                                  <span className="bg-gray-950 px-1.5 py-0.2 rounded border border-gray-800 text-gray-300">
                                    {s.source}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>

                              {/* Last Changed / Collected At */}
                              <td className="py-1 px-3 font-mono text-[10px] text-gray-400 whitespace-nowrap">
                                {s.collected_at ? (
                                  <span className="text-gray-400">
                                    {new Date(s.collected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>

                              {/* Description */}
                              <td className="py-1 px-3 text-gray-300 text-[11px] max-w-md truncate" title={s.short_desc}>
                                {s.short_desc || '-'}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500 italic">
                    No configuration parameters found matching filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
